// apps/client/src/pages/conversations/ConversationsInboxPage.tsx
//
// Inbox view for Page DMs. Mirrors LeadsPage.tsx for styling, tab
// affordance, and broker-family scoping. Row click → /leads/dm-inbox/:id.
//
// We deliberately do NOT render a message preview at v1 per the prompt:
// the preview field is broker-only visibility per APP 13 (canonical
// plan §1.5 lastMessagePreview), and rendering it costs an extra row
// of layout for marginal UX gain; brokers click in to read.
//
// Tab counts are derived from the ONE snapshot the hook holds. The page
// used to call useConversations twice (once for the tab, once for counts),
// which opened two live listeners over the same collection; the hook now
// returns both the filtered list and the full snapshot.
//
// Triage (mark read / close / reopen / reassign) goes through the
// `conversationAction` callable: `conversations` is `allow write: if false`
// in firestore.rules, so the client can never mutate these docs directly.
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  MessageSquare,
  RefreshCw,
  Inbox,
  UserCheck,
  Users,
  Archive,
  Check,
  RotateCcw,
} from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import {
  ConversationDoc,
  ConversationTab,
  isConversationBrokerFamily,
  useConversations,
} from '../../hooks/useConversations';

type OutletCtx = {
  userData?: {
    uid?: string;
    companyId?: string;
    role?: string;
    displayName?: string;
  };
};

type ConversationActionName = 'markRead' | 'close' | 'reopen';

const ALL_TABS: Array<{ key: ConversationTab; label: string; icon: React.FC<{ className?: string }> }> = [
  { key: 'assigned_to_me', label: 'Assigned to me', icon: UserCheck },
  { key: 'unassigned', label: 'Unassigned', icon: Inbox },
  { key: 'closed', label: 'Closed', icon: Archive },
  { key: 'all', label: 'All', icon: Users },
];

function formatRelative(ts: Timestamp | null): string {
  if (!ts) return '—';
  try {
    const millis = ts.toMillis();
    const now = Date.now();
    const diffMs = Math.max(0, now - millis);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return ts.toDate().toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function customerName(conv: ConversationDoc): string {
  const { firstName, lastName } = conv.participantProfile;
  const composed = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (composed) return composed;
  // Fall back to a redacted PSID — the raw PSID is page-scoped and not
  // human-meaningful, so we mask all but the last 6 chars per the same
  // pattern the backend uses in PII-safe logs (canonical §1.2).
  if (conv.psid) {
    const tail = conv.psid.slice(-6);
    return `PSID ******${tail}`;
  }
  return 'Unknown participant';
}

const ConversationsInboxPage: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const currentUid = userData?.uid;

  const role = userData?.role;
  const brokerScopedRole = isConversationBrokerFamily(role);

  const [tab, setTab] = useState<ConversationTab>('assigned_to_me');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { conversations, allConversations, loading, error } = useConversations(
    companyId,
    tab,
    currentUid,
    role
  );

  // A broker-family caller can only ever read threads assigned to them, so
  // "Unassigned" and "All" would be a duplicate of "Assigned to me" and an
  // always-empty tab respectively. Show them only the tabs that mean
  // something for their scope.
  const tabs = useMemo(
    () =>
      brokerScopedRole
        ? ALL_TABS.filter((t) => t.key === 'assigned_to_me' || t.key === 'closed')
        : ALL_TABS,
    [brokerScopedRole]
  );

  const runAction = useCallback(
    async (conversationId: string, action: ConversationActionName) => {
      if (!companyId) return;
      setPendingId(conversationId);
      setActionError(null);
      try {
        await httpsCallable(
          functions,
          'conversationAction'
        )({ action, companyId, conversationId });
        // No local state to patch: the Firestore listener delivers the
        // updated doc.
      } catch (err) {
        console.error('[ConversationsInboxPage] action failed', action, err);
        setActionError(
          err instanceof Error
            ? err.message
            : `Could not ${action} this conversation.`
        );
      } finally {
        setPendingId(null);
      }
    },
    [companyId]
  );

  const tabCounts = useMemo(() => {
    const out: Record<ConversationTab, number> = {
      assigned_to_me: 0,
      unassigned: 0,
      closed: 0,
      all: allConversations.length,
    };
    for (const c of allConversations) {
      if (c.assignedBrokerId && c.assignedBrokerId === currentUid)
        out.assigned_to_me += 1;
      if (!c.assignedBrokerId && c.status !== 'closed') out.unassigned += 1;
      if (c.status === 'closed') out.closed += 1;
    }
    return out;
  }, [allConversations, currentUid]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={MessageSquare}
        title="Conversations"
        subtitle="Page DM threads"
        badge="Beta"
        isRefreshing={loading}
      />

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => {
          const active = t.key === tab;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border inline-flex items-center gap-2 transition-colors ${
                active
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-900/40 border-gray-700 text-gray-300 hover:text-white hover:border-gray-500'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{t.label}</span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  active
                    ? 'bg-blue-800/70 text-blue-100'
                    : 'bg-gray-800 text-gray-300'
                }`}
              >
                {tabCounts[t.key]}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
          {actionError}
        </div>
      )}

      {loading && conversations.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Loading…
        </div>
      ) : conversations.length === 0 ? (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-10 text-center text-gray-400">
          <MessageSquare className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-sm">No conversations in this view yet.</p>
          <p className="text-xs text-gray-500 mt-2">
            Connect a Facebook Page in Settings → Connected Accounts and
            enable “Forward Page DMs to my VALUTTEN inbox” to start
            ingesting Messenger threads.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-3">Last message</th>
                <th className="text-left px-3 py-3">Customer</th>
                <th className="text-left px-3 py-3">Assigned</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {conversations.map((c) => {
                const unread =
                  c.unreadCount > 0 && c.lastMessageDirection === 'inbound';
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/leads/dm-inbox/${c.id}`)}
                    className="cursor-pointer hover:bg-white/[0.02]"
                  >
                    <td className="px-3 py-3 text-gray-300 text-xs whitespace-nowrap">
                      <div>{formatRelative(c.lastMessageAt)}</div>
                      <div className="text-[0.65rem] text-gray-500">
                        {c.lastMessageDirection === 'outbound'
                          ? 'Outbound'
                          : 'Inbound'}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-white">
                      <div className="flex items-center gap-2">
                        <span>{customerName(c)}</span>
                        {unread && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/40">
                            {c.unreadCount} new
                          </span>
                        )}
                      </div>
                      <div className="text-[0.65rem] text-gray-500">
                        Click to view
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs">
                      {c.assignedBrokerId ? (
                        c.assignedBrokerId === currentUid ? (
                          <span className="text-emerald-300">You</span>
                        ) : (
                          <span>{c.assignedBrokerId}</span>
                        )
                      ) : (
                        <span className="text-amber-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs capitalize">
                      {c.status}
                    </td>
                    <td
                      className="px-3 py-3 text-right text-xs"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        {unread && (
                          <button
                            type="button"
                            disabled={pendingId === c.id}
                            onClick={() => runAction(c.id, 'markRead')}
                            title="Mark as read"
                            className="inline-flex items-center gap-1 rounded border border-gray-600 px-2 py-1 text-gray-300 hover:text-white hover:border-gray-400 disabled:opacity-50"
                          >
                            <Check className="w-3 h-3" />
                            <span>Read</span>
                          </button>
                        )}
                        {c.status === 'closed' ? (
                          <button
                            type="button"
                            disabled={pendingId === c.id}
                            onClick={() => runAction(c.id, 'reopen')}
                            title="Reopen this conversation"
                            className="inline-flex items-center gap-1 rounded border border-gray-600 px-2 py-1 text-gray-300 hover:text-white hover:border-gray-400 disabled:opacity-50"
                          >
                            <RotateCcw className="w-3 h-3" />
                            <span>Reopen</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={pendingId === c.id}
                            onClick={() => runAction(c.id, 'close')}
                            title="Close this conversation"
                            className="inline-flex items-center gap-1 rounded border border-gray-600 px-2 py-1 text-gray-300 hover:text-white hover:border-gray-400 disabled:opacity-50"
                          >
                            <Archive className="w-3 h-3" />
                            <span>Close</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ConversationsInboxPage;
